import { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Lock, AlertCircle, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

const schema = z.object({
  identifier: z.string().min(4, "Enter email or mobile"),
  password: z.string().min(4, "Password too short"),
});

function landingFor(role) {
  if (role === "admin") return "/admin";
  if (role === "freelancer") return "/freelancer";
  return "/";
}

function isEmail(v) { return /@/.test(v); }

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { identifier: "", password: "" },
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (user) {
    // Ignore state.from here — LoginPage should not send an authenticated user to a route
    // that may reject their role and bounce back (infinite loop). Always land on the role's home.
    return <Navigate to={landingFor(user.role)} replace />;
  }

  const onSubmit = async (values) => {
    setError(null);
    try {
      const id = values.identifier.trim();
      // login helper accepts email; extend it inline for mobile
      const usr = await login(isEmail(id) ? id.toLowerCase() : id, values.password);
      // Prefer role home; only honor state.from if it isn't the /admin or /freelancer opposite role.
      const from = location.state?.from?.pathname;
      const roleHome = landingFor(usr.role);
      const safeFrom = from && !(usr.role === "freelancer" && from.startsWith("/admin"))
                            && !(usr.role === "admin" && from.startsWith("/freelancer"))
        ? from
        : roleHome;
      navigate(safeFrom, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block"><Logo variant="light" to="/" /></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" strokeWidth={1.6} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-slate-900">Sign In</h1>
              <p className="text-xs text-slate-500">Admin or Freelancer account</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email or Mobile</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="login-identifier"
                  placeholder="you@company.com or 98765 43210"
                  className="pl-9"
                  {...register("identifier")}
                />
              </div>
              {errors.identifier && <p role="alert" className="text-xs text-destructive">{errors.identifier.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                data-testid="login-password"
                {...register("password")}
              />
              {errors.password && <p role="alert" className="text-xs text-destructive">{errors.password.message}</p>}
            </div>

            {error && (
              <div role="alert" data-testid="login-error" className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="login-submit"
              className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-6 rounded-lg"
            >
              {isSubmitting ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Signing in…</span>
              ) : "Sign In"}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            Forgot your password? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
